import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import 'highcharts/modules/accessibility';

function ColumnChart({ height, values, categories, colors, options, yAxis }) {
  const containerRef = useRef(null);
  const valueDescriptionFormat = '{point.category} -  value {point.y}.';

  useEffect(() => {
    const yMax = values && Math.max(...values);

    Highcharts.chart(containerRef?.current, {
      chart: {
        type: 'column',
        grouping: false,
        backgroundColor: null,
        height,
        style: options.font,
      },
      accessibility: {
        enabled: true,
        pointDescriptionEnabled: true,
        keyboardNavigation: {
          enabled: true,
          focusBorder: {
            enabled: true,
          },
        },
        point: {
          describeNull: false,
          valueDescriptionFormat,
          descriptionFormatter(point) {
            return `${point.category}: ${point.y}`;
          },
        },
        description: `This column chart displays bioactivity data across different ${yAxis ? yAxis.toLowerCase() : 'activity'} ranges. Each column represents a category with its corresponding value.`,
        landmarkVerbosity: 'all',
        screenReaderSection: {
          beforeChartFormat:
            '<h3>Bioactivity Column Chart</h3>' +
            '<p>This chart shows the distribution of bioactivity data across different ranges. Navigate through the columns to explore individual data points.</p>',
        },
      },
      plotOptions: {
        ...options.plotOptions,
        column: {
          ...(options.plotOptions?.column || {}),
          borderWidth: 0.2,
          borderColor: '#2E2E2E',
          accessibility: {
            point: {
              valueDescriptionFormat,
            },
          },
        },
        series: {
          accessibility: {
            point: {
              valueDescriptionFormat,
            },
          },
        },
      },
      title: { text: '' },
      subtitle: { text: '' },
      credits: { enabled: false },
      xAxis: {
        type: 'category',
        categories,
        minorTickLength: 0,
        tickLength: 0,
        title: { text: 'Bioactivity ranges (based on pX(-log[activity])' },
        labels: { style: options.font },
        accessibility: {
          description: `Axis shows bioactivity categories: ${categories ? categories.join(', ') : 'various ranges'}.`,
        },
      },
      yAxis: {
        min: 0,
        max: yMax,
        allowDecimals: true,
        lineWidth: 1,
        title: { text: yAxis },
        labels: { style: options.font },
        accessibility: {
          description: `Scale from 0 to ${yMax || 'maximum'} representing ${yAxis ? yAxis.toLowerCase() : 'activity values'}.`,
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        headerFormat: '<b>{point.key}</b><br/>',
        pointFormat: '{point.y}',
        borderRadius: 0,
        shadow: false,
        style: options.font,
      },
      series: [
        {
          data: values.map((v) => {
            const hasNoData = v === null || v === undefined || v === 0;
            return hasNoData ? { y: v, accessibility: { enabled: false } } : v;
          }),
          dataLabels: { enabled: false },
        },
      ],
      colors,
    });
  }, [height, values, categories, colors, options, yAxis]);

  return <div ref={containerRef} />;
}

ColumnChart.propTypes = {
  height: PropTypes.string.isRequired,
  values: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
  options: PropTypes.object.isRequired,
  yAxis: PropTypes.string.isRequired,
};

export default ColumnChart;
